import { assertNever } from "@redotech/util/type";
import { JSXElementConstructor, memo } from "react";
import { Card, CardSize } from "../../card";
import { Flex } from "../../flex";
import { LoadingRedoAnimation } from "../../loading-redo-animation";
import { Text } from "../../text";
import { RedoBadge } from "../badge/redo-badge";
import * as settingsCardCss from "./settings-card.module.css";

export enum ChannelConnectionState {
  CONNECTED = "connected",
  PENDING = "pending",
  UNVERIFIED = "unverified",
  NOT_CONNECTED = "not_connected",
  LOADING = "loading",
}

export type SettingsCardWidth = "half" | "third" | "full";

export const SettingsCard = memo(function SettingsCard({
  name,
  description,
  Logo,
  SecondaryLogo,
  connectionState,
  bottomContent,
  buttonSection,
  customBackgroundColor,
  customBorderColor,
  customBorderRadius,
  cardWidth = "half",
}: {
  name: string;
  description: string;
  Logo?: JSXElementConstructor<any>;
  SecondaryLogo?: JSXElementConstructor<any>;
  connectionState?: ChannelConnectionState;
  buttonSection?: JSX.Element;
  bottomContent?: JSX.Element;
  customBackgroundColor?: string;
  customBorderColor?: string;
  customBorderRadius?: number;
  cardWidth?: SettingsCardWidth;
}) {
  const selectButton =
    connectionState === ChannelConnectionState.LOADING ? (
      <div className={settingsCardCss.spinnerContainer}>
        <LoadingRedoAnimation />
      </div>
    ) : (
      <div className={settingsCardCss.buttonContainer}>{buttonSection}</div>
    );

  const getContentClassName = () => {
    switch (cardWidth) {
      case "third":
        return settingsCardCss.contentThird;
      case "full":
        return settingsCardCss.contentFull;
      default:
        return settingsCardCss.content;
    }
  };

  return (
    <Card
      className={getContentClassName()}
      customBackgroundColor={customBackgroundColor}
      customBorderColor={customBorderColor}
      customBorderRadius={customBorderRadius}
      size={CardSize.MEDIUM}
    >
      <Flex className={settingsCardCss.contentWrapper} dir="column">
        <Flex justify="space-between">
          <Flex className={settingsCardCss.titleWrapper} gap="xl">
            <Flex dir="column">
              {Logo && (
                <div className={settingsCardCss.imageContainer}>
                  <Logo className={settingsCardCss.image} />
                </div>
              )}
              {SecondaryLogo && (
                <div className={settingsCardCss.imageContainer}>
                  <SecondaryLogo className={settingsCardCss.image} />
                </div>
              )}
            </Flex>
            <Flex dir="column" gap="xs">
              <Flex
                className={settingsCardCss.titleContent}
                dir="column"
                gap="xs"
              >
                <Flex align="center">
                  <Text fontSize="sm" fontWeight="bold">
                    {name}
                  </Text>
                </Flex>
                {connectionState !== undefined && (
                  <div>
                    <ConnectionStatePill state={connectionState} />
                  </div>
                )}
              </Flex>
              <Text fontSize="xs">{description}</Text>
            </Flex>
          </Flex>
        </Flex>
        {bottomContent}
        <div className={settingsCardCss.selectButton}>{selectButton}</div>
      </Flex>
    </Card>
  );
});

const ConnectionStatePill = memo(function ConnectionStatePill({
  state,
}: {
  state: ChannelConnectionState;
}) {
  switch (state) {
    case ChannelConnectionState.CONNECTED:
      return (
        <RedoBadge
          color="success"
          segmentLeading={{
            type: "icon",
            Icon: () => <div className={settingsCardCss.activeDot} />,
          }}
          size="xs"
          text="Connected"
        />
      );
    case ChannelConnectionState.PENDING:
      return <RedoBadge color="blue" size="xs" text="Pending" />;
    case ChannelConnectionState.UNVERIFIED:
      return <RedoBadge color="error" size="xs" text="Unverified" />;
    case ChannelConnectionState.NOT_CONNECTED:
      return <RedoBadge color="gray" size="xs" text="Not connected" />;
    case ChannelConnectionState.LOADING:
      return null;
    default:
      return assertNever(state);
  }
});
